import { useState } from 'react';
import { Control, FieldValues, Path } from 'react-hook-form';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form/form';
import { cn, SelectOptionGroup, useCurrencyFormatter } from '@liquidbudget/common';
import { Ban, Check } from 'lucide-react';
import { Button } from '../button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../command';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';

type Props<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  groups: SelectOptionGroup<string>[];
  isSmallScreen?: boolean;
  disabled?: boolean;
};

export function ExternalAccountField<T extends FieldValues>({
  control,
  name,
  groups,
  isSmallScreen = false,
  disabled = false,
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const formatCurrency = useCurrencyFormatter();

  const getSelectedDisplay = (groups: SelectOptionGroup<string>[], value?: string) => {
    if (!value) {
      return 'Link to an external bank account';
    }

    for (const group of groups) {
      const option = group.options.find((opt) => opt.value === value);
      if (option) {
        return `${group.label} - ${option.label}`;
      }
    }
    return 'Link to an external bank account';
  };

  return (
    <FormField<T>
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <div className="flex items-center justify-between mt-2">
            <FormLabel>External Bank</FormLabel>
            {groups.length === 0 && (
              <a
                href="/dashboard/bank-integration"
                className="text-sm text-muted-foreground underline hover:text-foreground"
              >
                Enable Bank Integration
              </a>
            )}
          </div>
          <Popover open={open && !disabled} onOpenChange={setOpen} modal>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  variant="outline"
                  role="combobox"
                  className={cn('w-full justify-between', !field.value && 'text-muted-foreground')}
                  disabled={disabled}
                >
                  <span className="truncate max-w-[calc(400px)]">{getSelectedDisplay(groups, field.value)}</span>
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]">
              <Command>
                {!isSmallScreen && <CommandInput placeholder="Search accounts..." />}
                <CommandList>
                  <CommandEmpty>No accounts found.</CommandEmpty>

                  {field.value && (
                    <CommandGroup>
                      <CommandItem
                        onSelect={() => {
                          field.onChange('');
                          setOpen(false);
                        }}
                        className="text-destructive"
                      >
                        <div className="flex items-center">
                          <Ban className="w-4 h-4 mr-2" />
                          Unlink bank account
                        </div>
                      </CommandItem>
                    </CommandGroup>
                  )}

                  {groups.map((group) => (
                    <CommandGroup key={group.label} heading={group.label}>
                      {group.options.map((option) => (
                        <CommandItem
                          key={option.value}
                          value={option.value}
                          onSelect={() => {
                            field.onChange(option.value);
                            setOpen(false);
                          }}
                          className="flex justify-between items-center"
                        >
                          <div className="flex items-center">
                            <Check
                              className={cn('mr-2 h-4 w-4', option.value === field.value ? 'opacity-100' : 'opacity-0')}
                            />
                            {option.label}
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {formatCurrency(option.aside ? parseFloat(option.aside) : 0)}
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ))}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
